/**
 * NewsletterTab — Admin interface for the GradLink Wednesday Digest.
 *
 * Sections:
 *  - Preview (live iframe of this week's digest)
 *  - This week's data summary
 *  - Sync subscribers to Resend Audience
 *  - Send test to a single email
 *  - Create + send Broadcast (immediate or scheduled)
 *  - Recent broadcasts log
 */
import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Mail, Send, RefreshCw, Eye, Users, Loader2, AlertTriangle,
  Calendar, CheckCircle, Sparkles,
} from 'lucide-react';

const DEFAULT_SUBJECT = "GradLink — Your Wednesday Digest is here";

export default function NewsletterTab() {
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);

  // Settings
  const [kickoff, setKickoff] = useState(true);
  const [foundingCta, setFoundingCta] = useState(true);
  const [mediaSpotlight, setMediaSpotlight] = useState(true);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [broadcastName, setBroadcastName] = useState('');

  // Send controls
  const [testEmail, setTestEmail] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  // Loading states for actions
  const [sendingTest, setSendingTest] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const html = await adminAPI.newsletterPreview({
        kickoff: kickoff ? 'true' : 'false',
        founding_cta: foundingCta ? 'true' : 'false',
        media_spotlight: mediaSpotlight ? 'true' : 'false',
      });
      setPreviewHtml(html);
    } catch (e) {
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [kickoff, foundingCta, mediaSpotlight]);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const r = await adminAPI.newsletterData();
      setData(r.data);
    } catch (e) {
      // non-blocking
    } finally {
      setLoadingData(false);
    }
  }, []);

  const loadBroadcasts = useCallback(async () => {
    try {
      const r = await adminAPI.newsletterBroadcasts();
      setBroadcasts(r.data.broadcasts || []);
    } catch (e) {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    loadPreview();
    loadData();
    loadBroadcasts();
  }, [loadPreview, loadData, loadBroadcasts]);

  const handleSync = async () => {
    setSyncing(true);
    const tId = toast.loading('Starting sync…');
    let totals = { added: 0, skipped: 0, failed: 0 };
    let lastSample = [];
    try {
      // Loop until backend says has_more=false. Each call processes ~80 users
      // (sized to fit Render's 30s gateway timeout).
      for (let i = 0; i < 50; i++) {
        const r = await adminAPI.newsletterSync({});
        totals.added += r.data.added || 0;
        totals.skipped += r.data.skipped || 0;
        totals.failed += r.data.failed || 0;
        if ((r.data.errors_sample || []).length) lastSample = r.data.errors_sample;
        toast.loading(
          `Syncing… ${r.data.total_synced}/${r.data.total_users} (+${totals.added} added, ${totals.failed} failed)`,
          { id: tId }
        );
        if (!r.data.has_more) break;
      }
      if (totals.failed > 0) {
        const firstErr = lastSample[0] ? ` First error: ${lastSample[0].error}` : '';
        toast.error(`Sync done with errors: +${totals.added} added, ${totals.failed} failed, ${totals.skipped} skipped.${firstErr}`, { id: tId, duration: 10000 });
      } else {
        toast.success(`Synced: +${totals.added} added, ${totals.skipped} skipped`, { id: tId });
      }
      loadBroadcasts();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Sync failed';
      toast.error(`Sync error: ${detail}`, { id: tId, duration: 12000 });
    } finally {
      setSyncing(false);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSendingTest(true);
    try {
      const r = await adminAPI.newsletterTestSend({
        email: testEmail,
        kickoff,
        founding_cta: foundingCta,
        media_spotlight: mediaSpotlight,
        subject,
      });
      toast.success(`Test sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSend = async (sendNow) => {
    if (!subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    const confirmMsg = sendNow
      ? `Send the digest to all subscribers NOW?\n\nSubject: "${subject}"\n\nThis cannot be undone.`
      : `Create the broadcast in Resend (scheduled for: ${scheduledAt || 'manual send later'})?`;
    if (!window.confirm(confirmMsg)) return;
    setCreating(true);
    try {
      const payload = {
        subject,
        name: broadcastName || `Wednesday Digest — ${new Date().toISOString().slice(0, 10)}`,
        kickoff,
        founding_cta: foundingCta,
        media_spotlight: mediaSpotlight,
        send_now: sendNow,
      };
      if (!sendNow && scheduledAt) payload.scheduled_at = scheduledAt;
      const r = await adminAPI.newsletterSend(payload);
      if (sendNow) {
        toast.success(`Broadcast sent! ID: ${r.data.broadcast_id}`);
      } else {
        toast.success(`Broadcast created${scheduledAt ? ' & scheduled' : ''} — ID: ${r.data.broadcast_id}`);
      }
      loadBroadcasts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-mint-fresh flex items-center justify-center">
            <Mail className="w-5 h-5 text-dark-slate" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-dark-slate">Wednesday Digest</h2>
            <p className="text-sm text-slate-gray">Weekly newsletter to all GradLink members</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => { loadPreview(); loadData(); }}
          className="rounded-full"
          data-testid="newsletter-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingPreview ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* This week's data summary */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h3 className="text-sm font-semibold text-slate-gray uppercase tracking-wide mb-4">
          This week ({data?.week?.label || '—'})
        </h3>
        {loadingData ? (
          <div className="flex items-center text-slate-gray"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</div>
        ) : data ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat label="New members" value={data.stats.new_users} sub={`of ${data.stats.total_users.toLocaleString()} total`} />
            <Stat label="New jobs" value={data.stats.new_jobs} sub={`${data.jobs.length} shown`} />
            <Stat label="Providers" value={data.stats.total_providers} sub="verified" />
            <Stat label="Founding Providers" value={(data.founding_providers || []).length} sub="in digest spotlight" />
            <Stat label="HBCUs represented" value={data.hbcu_count || 0} sub="across all members" />
            <Stat label="Events" value={data.events.length} sub="upcoming" />
          </div>
        ) : null}
      </div>

      {/* Settings + Send block */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-gray uppercase tracking-wide">Settings</h3>

          <div>
            <Label htmlFor="nl-subject" className="text-sm">Subject line</Label>
            <Input
              id="nl-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 rounded-xl"
              data-testid="newsletter-subject"
            />
          </div>

          <div>
            <Label htmlFor="nl-name" className="text-sm">Internal name (Resend dashboard)</Label>
            <Input
              id="nl-name"
              value={broadcastName}
              onChange={(e) => setBroadcastName(e.target.value)}
              placeholder={`Wednesday Digest — ${new Date().toISOString().slice(0, 10)}`}
              className="mt-1 rounded-xl"
              data-testid="newsletter-name"
            />
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <label className="flex items-center gap-2 text-sm text-dark-slate cursor-pointer">
              <input
                type="checkbox"
                checked={kickoff}
                onChange={(e) => setKickoff(e.target.checked)}
                data-testid="newsletter-kickoff"
              />
              <Sparkles className="w-4 h-4 text-mint-fresh" />
              First-time / kickoff edition (welcomes the reader to the digest)
            </label>
            <label className="flex items-center gap-2 text-sm text-dark-slate cursor-pointer">
              <input
                type="checkbox"
                checked={foundingCta}
                onChange={(e) => setFoundingCta(e.target.checked)}
                data-testid="newsletter-founding-cta"
              />
              Show Founding Provider callout (first ~8 weeks)
            </label>
            <label className="flex items-center gap-2 text-sm text-dark-slate cursor-pointer">
              <input
                type="checkbox"
                checked={mediaSpotlight}
                onChange={(e) => setMediaSpotlight(e.target.checked)}
                data-testid="newsletter-media-spotlight"
              />
              Show HBCU Radio + Podcasts spotlight
            </label>
          </div>
        </div>

        {/* Send controls */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
          <h3 className="text-sm font-semibold text-slate-gray uppercase tracking-wide">Send</h3>

          {/* Sync */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">1. Sync subscribers</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                className="rounded-full"
                data-testid="newsletter-sync"
              >
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Sync to Resend
              </Button>
            </div>
            <p className="text-xs text-slate-gray">Pushes new MongoDB users into the Resend Audience. Safe to run repeatedly.</p>
          </div>

          {/* Test */}
          <div className="border-t border-slate-100 pt-4">
            <Label className="text-sm block mb-2">2. Test send</Label>
            <div className="flex gap-2">
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="rounded-xl"
                data-testid="newsletter-test-email"
              />
              <Button
                onClick={handleTestSend}
                disabled={sendingTest}
                className="rounded-full bg-dark-slate text-white"
                data-testid="newsletter-test-send"
              >
                {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Test
              </Button>
            </div>
            <p className="text-xs text-slate-gray mt-1">Open the test email and check for broken images or layout glitches before broadcasting.</p>
          </div>

          {/* Broadcast */}
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <Label className="text-sm block">3. Send to everyone</Label>
            <div>
              <Label htmlFor="nl-sched" className="text-xs text-slate-gray">Schedule (optional, ISO 8601 or natural: "in 1 hour")</Label>
              <Input
                id="nl-sched"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                placeholder="leave blank to create as a draft"
                className="mt-1 rounded-xl"
                data-testid="newsletter-sched"
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                onClick={() => handleSend(false)}
                disabled={creating}
                variant="outline"
                className="rounded-full"
                data-testid="newsletter-create-draft"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                {scheduledAt ? 'Schedule' : 'Create draft'}
              </Button>
              <Button
                onClick={() => handleSend(true)}
                disabled={creating}
                className="rounded-full bg-dark-slate text-white"
                data-testid="newsletter-send-now"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send to all subscribers
              </Button>
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Always run a test send first. Sending to all subscribers is logged but cannot be recalled.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-gray uppercase tracking-wide flex items-center gap-2">
            <Eye className="w-4 h-4" /> Live preview
          </h3>
          <p className="text-xs text-slate-gray">Reflects current settings · personalized name shown as "Friend"</p>
        </div>
        {loadingPreview ? (
          <div className="h-[600px] flex items-center justify-center text-slate-gray">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading preview…
          </div>
        ) : (
          <iframe
            title="Digest preview"
            srcDoc={previewHtml}
            className="w-full h-[900px] rounded-xl border border-slate-200 bg-slate-50"
            data-testid="newsletter-iframe"
          />
        )}
      </div>

      {/* Recent broadcasts */}
      {broadcasts.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h3 className="text-sm font-semibold text-slate-gray uppercase tracking-wide mb-4">Recent broadcasts</h3>
          <div className="space-y-2">
            {broadcasts.map((b) => (
              <div key={b.broadcast_id} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-sm">
                <div>
                  <div className="font-medium text-dark-slate">{b.name}</div>
                  <div className="text-xs text-slate-gray">{b.subject}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-gray">{new Date(b.created_at).toLocaleString()}</div>
                  <div className="text-xs">
                    {b.send_now ? (
                      <span className="text-emerald-600 inline-flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> sent</span>
                    ) : b.scheduled_at ? (
                      <span className="text-blue-600">scheduled: {b.scheduled_at}</span>
                    ) : (
                      <span className="text-slate-gray">draft</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-2xl font-bold text-dark-slate">{value ?? 0}</div>
      <div className="text-xs text-slate-gray font-medium uppercase tracking-wide">{label}</div>
      {sub && <div className="text-xs text-slate-gray mt-0.5">{sub}</div>}
    </div>
  );
}
